(() => {
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  let activeDrawer = null;
  let pageLockDrawer = null;
  let inertedElements = [];

  function getFocusable(container) {
    return [...container.querySelectorAll(focusableSelector)].filter((element) => !element.hidden && !element.closest('[hidden]'));
  }

  function lockPage(drawer) {
    if (pageLockDrawer && pageLockDrawer !== drawer) return false;
    if (pageLockDrawer === drawer) return true;
    pageLockDrawer = drawer;
    document.documentElement.classList.add('drawer-open');
    inertedElements = [];
    const remember = (element) => {
      if (inertedElements.some((entry) => entry.element === element) || ['SCRIPT', 'STYLE'].includes(element.tagName)) return;
      inertedElements.push({ element, inert: element.inert, ariaHidden: element.getAttribute('aria-hidden') });
    };
    let branchElement = drawer;
    while (branchElement && branchElement !== document.body && branchElement.parentElement) {
      [...branchElement.parentElement.children].filter((sibling) => sibling !== branchElement).forEach(remember);
      branchElement = branchElement.parentElement;
    }
    inertedElements.forEach(({ element }) => {
      element.inert = true;
      element.setAttribute('aria-hidden', 'true');
    });
    return true;
  }

  function unlockPage(drawer) {
    if (pageLockDrawer !== drawer) return;
    document.documentElement.classList.remove('drawer-open');
    inertedElements.forEach(({ element, inert, ariaHidden }) => {
      element.inert = inert;
      if (ariaHidden === null) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', ariaHidden);
    });
    inertedElements = [];
    pageLockDrawer = null;
  }

  class DrawerElement extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.backdrop = this.querySelector('[data-drawer-backdrop]');
      this.state = 'closed';
      this.openToken = 0;
      this.onKeydown = this.handleKeydown.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.openToken = (this.openToken || 0) + 1;
      window.cancelAnimationFrame?.(this.openFrame);
      window.clearTimeout(this.closeTimer);
      unlockPage(this);
      if (activeDrawer === this) activeDrawer = null;
      this.hidden = true;
      this.classList.remove('is-open', 'is-closing');
      this.state = 'closed';
      this.setAttribute('aria-hidden', 'true');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger = null;
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('click', this.onClick);
    }

    get opened() {
      return this.state === 'opening' || this.state === 'open';
    }

    open(trigger) {
      document.querySelectorAll('navigation-drawer, predictive-search, cart-drawer').forEach((drawer) => {
        if (drawer !== this && drawer.state !== 'closed') drawer.close({ restoreFocus: false, immediate: true });
      });
      window.clearTimeout(this.closeTimer);
      window.cancelAnimationFrame?.(this.openFrame);
      const openingToken = ++this.openToken;
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      this.state = 'opening';
      this.classList.remove('is-closing');
      this.setAttribute('aria-hidden', 'false');
      activeDrawer = this;
      if (!lockPage(this)) {
        this.close({ restoreFocus: false, immediate: true });
        return;
      }
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'true');
      this.openFrame = window.requestAnimationFrame(() => {
        if (this.state !== 'opening' || openingToken !== this.openToken) return;
        this.classList.add('is-open');
        this.state = 'open';
        const target = this.initialFocusTarget();
        target.focus();
      });
      this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    }

    close({ restoreFocus = true, immediate = false } = {}) {
      if (this.state === 'closed') return;
      this.openToken += 1;
      window.cancelAnimationFrame?.(this.openFrame);
      this.classList.remove('is-open');
      this.classList.add('is-closing');
      this.state = 'closing';
      this.setAttribute('aria-hidden', 'true');
      if (this.trigger && this.trigger.setAttribute) this.trigger.setAttribute('aria-expanded', 'false');
      if (activeDrawer === this) {
        activeDrawer = null;
        unlockPage(this);
      }
      if (restoreFocus && this.trigger && this.trigger.isConnected) this.trigger.focus();
      this.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
      if (immediate || this.motionReduced()) this.finishClose();
      else this.closeTimer = window.setTimeout(() => this.finishClose(), 280);
    }

    finishClose() {
      window.clearTimeout(this.closeTimer);
      this.hidden = true;
      this.classList.remove('is-closing');
      this.state = 'closed';
    }

    initialFocusTarget() {
      return getFocusable(this.panel)[0] || this.panel;
    }

    motionReduced() {
      return document.documentElement.classList.contains('motion-reduced') || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    handleClick(event) {
      if (event.target.closest('[data-drawer-close]') || event.target === this.backdrop) this.close();
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab' || !this.opened) return;
      const focusable = getFocusable(this.panel);
      if (!focusable.length) {
        event.preventDefault();
        this.panel.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  class NavigationDrawer extends DrawerElement {
    connectedCallback() {
      super.connectedCallback();
      this.rootPanel = this.querySelector('[data-nav-root]');
      this.onNavigationClick = (event) => {
        const next = event.target.closest('[data-nav-submenu-trigger]');
        if (next) {
          event.preventDefault();
          this.showPanel(next.dataset.navSubmenuTrigger);
        }
        if (event.target.closest('[data-nav-back]')) {
          event.preventDefault();
          this.showRoot({ focusTrigger: true });
        }
      };
      this.addEventListener('click', this.onNavigationClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onNavigationClick);
      super.disconnectedCallback();
    }

    open(trigger) {
      this.showRoot();
      super.open(trigger);
    }

    showPanel(name) {
      if (this.rootPanel) this.rootPanel.hidden = true;
      this.querySelectorAll('[data-nav-panel]').forEach((panel) => { panel.hidden = panel.dataset.navPanel !== name; });
      this.querySelectorAll('[data-nav-submenu-trigger]').forEach((button) => button.setAttribute('aria-expanded', String(button.dataset.navSubmenuTrigger === name)));
      const panel = this.querySelector(`[data-nav-panel="${name}"]`);
      this.submenuTrigger = this.querySelector(`[data-nav-submenu-trigger="${name}"]`);
      const back = panel && panel.querySelector('[data-nav-back]');
      if (back) back.focus();
    }

    showRoot({ focusTrigger = false } = {}) {
      if (this.rootPanel) this.rootPanel.hidden = false;
      this.querySelectorAll('[data-nav-panel]').forEach((panel) => { panel.hidden = true; });
      this.querySelectorAll('[data-nav-submenu-trigger]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (focusTrigger && this.submenuTrigger) this.submenuTrigger.focus();
    }
  }

  class PredictiveSearch extends DrawerElement {
    connectedCallback() {
      super.connectedCallback();
      this.input = this.querySelector('[data-predictive-search-input]');
      this.results = this.querySelector('[data-predictive-search-results]');
      this.status = this.querySelector('[data-predictive-search-status]');
      this.timeout = null;
      this.searchSequence = 0;
      this.onInput = () => this.queueSearch();
      if (this.input) this.input.addEventListener('input', this.onInput);
    }

    disconnectedCallback() {
      window.clearTimeout(this.timeout);
      this.cancelSearch();
      if (this.input) this.input.removeEventListener('input', this.onInput);
      super.disconnectedCallback();
    }

    initialFocusTarget() {
      return this.input || super.initialFocusTarget();
    }

    queueSearch() {
      window.clearTimeout(this.timeout);
      const query = this.input.value.trim();
      if (!query) {
        this.cancelSearch();
        this.results.replaceChildren();
        this.status.textContent = '';
        return;
      }
      this.timeout = window.setTimeout(() => this.search(query), 180);
    }

    async search(query) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const requestSequence = ++this.searchSequence;
      this.status.textContent = this.dataset.loadingText;
      try {
        const url = new URL(this.dataset.searchUrl, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', this.dataset.searchLimit);
        const response = await fetch(url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error('request failed');
        const data = await response.json();
        if (requestSequence !== this.searchSequence) return;
        const products = data.resources?.results?.products || [];
        this.results.replaceChildren(...products.map((product) => {
          const link = document.createElement('a');
          link.href = product.url;
          link.textContent = product.title;
          return link;
        }));
        this.status.textContent = products.length ? (products.length === 1 ? this.dataset.resultsCountOne : this.dataset.resultsCountOther).replace('{{ count }}', products.length) : this.dataset.noResults;
      } catch (_) {
        if (requestSequence !== this.searchSequence || this.abortController.signal.aborted) return;
        this.status.textContent = this.dataset.unavailableText;
      }
    }

    cancelSearch() {
      this.searchSequence += 1;
      this.abortController?.abort();
    }
  }

  class CartDrawer extends DrawerElement {
    connectedCallback() {
      super.connectedCallback();
      this.items = this.querySelector('[data-cart-items]');
      this.empty = this.querySelector('[data-cart-empty]');
      this.form = this.querySelector('[data-cart-form]');
      this.whatsapp = this.querySelector('[data-whatsapp-cart]');
      this.error = this.querySelector('[data-cart-error]');
      this.onCartClick = (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) this.changeLine(remove.dataset.cartKey, 0);
      };
      this.onCartChange = (event) => {
        const quantity = event.target.closest('[data-cart-quantity]');
        if (quantity) this.changeLine(quantity.dataset.cartKey, Number(quantity.value));
      };
      this.addEventListener('click', this.onCartClick);
      this.addEventListener('change', this.onCartChange);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onCartClick);
      this.removeEventListener('change', this.onCartChange);
      super.disconnectedCallback();
    }

    renderContents(cart) {
      const isEmpty = !cart.item_count;
      if (this.empty) this.empty.hidden = !isEmpty;
      if (this.form) this.form.hidden = isEmpty;
      if (this.items) {
        if (isEmpty) this.items.replaceChildren();
        else if (Array.isArray(cart.items)) this.items.replaceChildren(...cart.items.map((item) => this.lineElement(item, cart.currency)));
      }
      this.querySelectorAll('[data-cart-total]').forEach((total) => { total.textContent = this.money(cart.total_price, cart.currency); });
      document.querySelectorAll('[data-cart-count]').forEach((count) => { count.textContent = String(cart.item_count || 0); });
      this.updateWhatsAppLink(cart);
      this.error.hidden = true;
      this.error.textContent = '';
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    }

    updateWhatsAppLink(cart) {
      if (!this.whatsapp || !cart) return;
      const data = this.whatsapp.dataset;
      const lines = [data.whatsappIntro || 'Hello Maison La Muse,', ...(cart.items || []).map((item) => {
        const title = item.product_title || item.title || '';
        const options = item.variant_title && item.variant_title !== 'Default Title' ? ` — ${item.variant_title}` : '';
        return `- ${title}${options} × ${item.quantity} — ${this.money(item.final_line_price, cart.currency)}`;
      }), `${data.whatsappTotal || 'Total'}: ${this.money(cart.total_price, cart.currency)}`];
      const base = data.whatsappUrl || 'https://wa.me/212714554908';
      const separator = base.includes('?') ? '&' : '?';
      this.whatsapp.href = `${base}${separator}text=${encodeURIComponent(lines.join('\n'))}`;
    }

    renderError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
    }

    announce(message) {
      const status = this.querySelector('[data-cart-status]');
      if (!status) return;
      status.textContent = message;
      status.hidden = false;
    }

    lineElement(item, currency) {
      const article = document.createElement('article');
      article.className = 'cart-line-item';
      article.dataset.cartLine = item.key;
      const imageLink = document.createElement('a');
      imageLink.className = 'cart-line-item__image';
      imageLink.href = item.url || this.dataset.cartUrl;
      const imageUrl = item.featured_image?.url || item.image;
      if (imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = item.product_title || item.title || '';
        image.loading = 'lazy';
        imageLink.append(image);
      }
      const content = document.createElement('div');
      content.className = 'cart-line-item__content';
      const title = document.createElement('a');
      title.href = item.url || this.dataset.cartUrl;
      title.textContent = item.product_title || item.title;
      content.append(title);
      if (item.variant_title && item.variant_title !== 'Default Title') {
        const variant = document.createElement('p');
        variant.textContent = item.variant_title;
        content.append(variant);
      }
      const price = document.createElement('p');
      price.textContent = this.money(item.final_line_price, currency);
      content.append(price);
      const quantityId = `CartQuantity-${item.key}`;
      const label = document.createElement('label');
      label.htmlFor = quantityId;
      label.className = 'visually-hidden';
      label.textContent = this.dataset.quantityLabel.replace('{{ title }}', item.product_title || item.title || '');
      const quantity = document.createElement('input');
      quantity.id = quantityId;
      quantity.type = 'number'; quantity.min = '0'; quantity.value = item.quantity; quantity.dataset.cartQuantity = ''; quantity.dataset.cartKey = item.key;
      const remove = document.createElement('button');
      remove.type = 'button'; remove.dataset.cartRemove = ''; remove.dataset.cartKey = item.key;
      remove.textContent = this.dataset.removeLabel;
      const removeContext = document.createElement('span');
      removeContext.className = 'visually-hidden';
      removeContext.textContent = ` ${item.product_title || item.title || ''}`;
      remove.append(removeContext);
      content.append(label, quantity, remove);
      article.append(imageLink, content);
      return article;
    }

    money(amount = 0, currency = 'MAD') {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency }).format(amount / 100);
    }

    async changeLine(key, quantity) {
      try {
        const response = await fetch(this.dataset.cartChangeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
        if (!response.ok) throw new Error('request failed');
        const cart = await response.json();
        this.renderContents(cart);
        this.announce(this.dataset.updatedMessage || '');
      } catch (_) {
        this.renderError(this.dataset.updateError);
      }
    }
  }

  if (!customElements.get('navigation-drawer')) customElements.define('navigation-drawer', NavigationDrawer);
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    const drawer = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(trigger);
  });
})();
